import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { useAuth } from "../AuthContext";
import useBlockedUsers from "../hooks/useBlockedUsers";

/**
 * Account → Privacy & account settings: email preferences, blocked users, download my data,
 * delete my account. (DPDP Act, GDPR Art. 15/17/20, CCPA right to know/delete, CAN-SPAM.)
 */
export default function AccountSettingsPanel() {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { blocked, unblock } = useBlockedUsers();

  const [prefs, setPrefs] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const usesPassword = (user?.provider || "email").toLowerCase() === "email";

  useEffect(() => {
    api.get("/api/email/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs(null));
  }, []);

  const togglePref = async (key) => {
    setErr("");
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      const r = await api.put("/api/email/preferences", { [key]: next[key] });
      setPrefs(r.data);
    } catch {
      setPrefs(prefs);
      setErr("Could not save your email preferences. Please try again.");
    }
  };

  const downloadData = async () => {
    setErr(""); setMsg(""); setExporting(true);
    try {
      const r = await api.get("/api/account/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `saatsaheli-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg("Your data file has been downloaded.");
    } catch (e) {
      setErr(e.response?.data?.error || "Could not prepare your data. Please try again.");
    }
    setExporting(false);
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    setErr("");
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      setErr('Type DELETE to confirm.');
      return;
    }
    if (usesPassword && !password) {
      setErr("Enter your password to confirm.");
      return;
    }
    setDeleting(true);
    try {
      await api.post("/api/account/delete", { confirm: "DELETE", password: usesPassword ? password : undefined });
      logout();
      navigate("/?accountDeleted=1");
    } catch (e2) {
      setErr(e2.response?.data?.error || "Could not delete your account. Please try again or contact us.");
      setDeleting(false);
    }
  };

  return (
    <section className="acct-settings" id="settings" aria-labelledby="acct-settings-heading">
      <h2 id="acct-settings-heading">Privacy &amp; account settings</h2>
      {msg && <p className="acct-settings-ok" role="status">{msg}</p>}
      {err && <p className="acct-error" role="alert">{err}</p>}

      <h3>Email preferences</h3>
      {prefs ? (
        <div className="acct-settings-prefs">
          <label>
            <input type="checkbox" checked={!!prefs.comments} onChange={() => togglePref("comments")} />
            <span>Email me when someone comments on my content</span>
          </label>
          <label>
            <input type="checkbox" checked={!!prefs.messages} onChange={() => togglePref("messages")} />
            <span>Email me when someone sends me a private message</span>
          </label>
          <p className="acct-settings-note">Essential emails (password resets, receipts, order and subscription confirmations) are always sent.</p>
        </div>
      ) : (
        <p className="acct-settings-note">Loading…</p>
      )}

      <h3>Blocked people</h3>
      {blocked.length === 0 ? (
        <p className="acct-settings-note">You haven't blocked anyone. Use "Block" on someone's comment or profile to stop their messages and hide their comments and content from you.</p>
      ) : (
        <ul className="acct-settings-blocked">
          {blocked.map((b) => (
            <li key={b.userId}>
              <span>{b.name}</span>
              <button type="button" onClick={() => unblock(b.userId)}>Unblock</button>
            </li>
          ))}
        </ul>
      )}

      <h3>Your data</h3>
      <p className="acct-settings-note">Download a copy of your account details, content, comments, messages, orders and payments as a file.</p>
      <button type="button" className="acct-profile-link" onClick={downloadData} disabled={exporting}>
        {exporting ? "Preparing…" : "Download my data"}
      </button>

      <h3>Delete account</h3>
      {isSuperAdmin ? (
        <p className="acct-settings-note">The superadmin account can't be deleted from here.</p>
      ) : !showDelete ? (
        <button type="button" className="acct-settings-danger" onClick={() => setShowDelete(true)}>Delete my account…</button>
      ) : (
        <form className="acct-settings-delete" onSubmit={deleteAccount}>
          <p>
            This closes your account straight away: you are signed out, your profile and content are hidden, and your
            name, email and profile details are removed. We permanently delete the rest within 30 days, except
            payment and order records the law requires us to keep. <strong>This can't be undone.</strong>
          </p>
          <label>
            Type <strong>DELETE</strong> to confirm
            <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete="off" />
          </label>
          {usesPassword && (
            <label>
              Your password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </label>
          )}
          <div className="acct-settings-delete-actions">
            <button type="submit" className="acct-settings-danger" disabled={deleting}>
              {deleting ? "Deleting…" : "Permanently delete my account"}
            </button>
            <button type="button" onClick={() => { setShowDelete(false); setConfirmText(""); setPassword(""); setErr(""); }}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
